import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  FiHome, FiLogOut, FiShield, FiLayers, FiUserCheck, FiCheckSquare, FiActivity,
  FiFolder, FiUsers, FiTool, FiShoppingCart, FiClipboard, FiFileText, FiBox, FiDollarSign, FiMail, FiBarChart2, FiArchive, FiTruck, FiSettings
} from 'react-icons/fi';
import './Sidebar.css';

const NAV_ITEMS = [
  { to: '/dashboard', icon: <FiHome />, label: 'Dashboard', moduleKey: 'dashboard' },
  { to: '/projects', icon: <FiFolder />, label: 'Projects', moduleKey: 'projects' },
  { to: '/clients', icon: <FiUsers />, label: 'Customers', moduleKey: 'clients' },
  { to: '/material-planning', icon: <FiBox />, label: 'Material Plan', moduleKey: 'material-planning' },
  { to: '/quotations', icon: <FiFileText />, label: 'Quotations', moduleKey: 'quotations' },
  { to: '/customer-quotations', icon: <FiFileText />, label: 'Customer Quotation', moduleKey: 'customer-quotations' },
  { to: '/customer-invoices', icon: <FiFileText />, label: 'Customer Invoice', moduleKey: 'customer-invoices' },
  { to: '/purchase', icon: <FiShoppingCart />, label: 'Purchase', moduleKey: 'purchase' },
  { to: '/task-planner', icon: <FiClipboard />, label: 'Task Planner', moduleKey: 'task-planner' },
  { to: '/site-execution', icon: <FiTool />, label: 'Site Execution', moduleKey: 'site-execution' },
  { to: '/quality-check', icon: <FiShield />, label: 'Quality Check', moduleKey: 'quality-check' },
  { to: '/finance', icon: <FiDollarSign />, label: 'Finance', moduleKey: 'finance' },
  { to: '/reports', icon: <FiBarChart2 />, label: 'Reports', moduleKey: 'reports' },
  { to: '/attendance', icon: <FiCheckSquare />, label: 'Attendance', moduleKey: 'attendance' },
];

const ADMIN_ITEMS = [
  { to: '/customer-orders', icon: <FiTruck />, label: 'Customer Orders', moduleKey: 'customer-orders', section: 'Masters' },
  { to: '/workshop', icon: <FiSettings />, label: 'Workshop', moduleKey: 'workshop-jobs', section: 'Masters' },
  { to: '/masters/stock', icon: <FiArchive />, label: 'Stock Master', moduleKey: 'stock-items', section: 'Masters' },
  { to: '/users', icon: <FiUserCheck />, label: 'Users', moduleKey: 'users', section: 'Admin' },
  { to: '/roles', icon: <FiShield />, label: 'Roles', moduleKey: 'roles', section: 'Admin' },
  { to: '/modules', icon: <FiLayers />, label: 'Modules', moduleKey: 'modules', section: 'Admin' },
  { to: '/activity-logs', icon: <FiActivity />, label: 'Activity Logs', moduleKey: 'activity-logs', section: 'Admin' },
  { to: '/mail-logs', icon: <FiMail />, label: 'Mail Logs', moduleKey: 'mail-logs', section: 'Admin' },
];

export default function Sidebar({ collapsed, setCollapsed, mobileOpen, isMobile }) {
  const { user, logout, canAccess } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate('/login'); };

  const visibleNav = NAV_ITEMS.filter(item => !item.moduleKey || canAccess(item.moduleKey));
  const masterItems = ADMIN_ITEMS.filter(i => i.section === 'Masters' && canAccess(i.moduleKey));
  const adminItems = ADMIN_ITEMS.filter(i => i.section === 'Admin' && canAccess(i.moduleKey));

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${isMobile && mobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-logo">
        {collapsed
          ? <img src="/mini_logo.png" alt="Logo" className="sidebar-mini-logo" />
          : <img src="/cmpy_logo.png" alt="Creative Glass Tec" className="sidebar-company-logo" />
        }
      </div>

      <nav className="sidebar-nav">
        {visibleNav.map(item => (
          <NavLink key={item.to} to={item.to} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title={collapsed ? item.label : ''}>
            {item.icon}
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}

        {masterItems.length > 0 && (
          <>
            {!collapsed && <div className="nav-section-label">Masters</div>}
            {collapsed && <div className="nav-section-divider" />}
            {masterItems.map(item => (
              <NavLink key={item.to} to={item.to} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title={collapsed ? item.label : ''}>
                {item.icon}
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </>
        )}

        {adminItems.length > 0 && (
          <>
            {!collapsed && <div className="nav-section-label">Admin</div>}
            {collapsed && <div className="nav-section-divider" />}
            {adminItems.map(item => (
              <NavLink key={item.to} to={item.to} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title={collapsed ? item.label : ''}>
                {item.icon}
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="sidebar-footer">
        <div className="user-info">
          <div className="user-avatar">{user?.name?.[0]?.toUpperCase()}</div>
          {!collapsed && (
            <div>
              <p className="user-name">{user?.name}</p>
              <p className="user-role">{user?.role?.name || user?.role}</p>
            </div>
          )}
        </div>
        <button className="logout-btn" onClick={handleLogout} title="Logout">
          <FiLogOut />
        </button>
      </div>
    </aside>
  );
}
